// ============================================================
// Datei: VolunteerList.jsx
// PURPOSE:
// Anzeige der Helfer-Kacheln inkl. Edit & Delete
//
// ÄNDERUNGEN:
// - Wrapper expandiert jetzt bei offenem Modal über Klasse "modal-open"
// - Create-Modal bleibt lokal in VolunteerList gerendert
// - EditVolunteerModal bleibt lokal
// - bestehende Logik nur minimal angepasst
// - Rollen-Farbcodes aus alter Admin-Liste ergänzt
// ============================================================

import React, { useMemo, useState } from "react";
import Api from "../Api";
import ConfirmDeleteModal from "./modals/ConfirmDeleteModal";
import EditVolunteerModal from "./EditVolunteerModal";
import ManualVolunteerRegisterForm from "./manualVolunteerRegisterForm/ManualVolunteerRegisterForm";
import "../styles/volunteerList.css";
import "../styles/global.css";

const ADD_ICON = `${process.env.PUBLIC_URL}/icons/addButton_icon.svg`;

// ------------------------------------------------------------
// Rollen-Konstante
// role_id = 3 bedeutet SUPERHELFER
// ------------------------------------------------------------
const ROLE_ID_SUPERHELFER = 3;

// ------------------------------------------------------------
// Hilfsfunktion:
// Liest die roleId robust aus verschiedenen DTO-Strukturen
// ------------------------------------------------------------
const getVolunteerRoleId = (volunteer) => {
	if (!volunteer) return null;

	if (volunteer.roleId != null) return Number(volunteer.roleId);
	if (volunteer.role_id != null) return Number(volunteer.role_id);

	if (volunteer.role?.roleId != null) return Number(volunteer.role.roleId);
	if (volunteer.role?.role_id != null) return Number(volunteer.role.role_id);
	if (volunteer.role?.id != null) return Number(volunteer.role.id);

	return null;
};

// ------------------------------------------------------------
// Farbcodes wie in deiner alten List.jsx
// ------------------------------------------------------------
const getRoleColor = (roleId) => {
	switch (Number(roleId)) {
		case 1:
			return "#00CED1"; // ADMIN 
		case 2:
			return "#F4A62A"; // OK
		case 3:
			return "#8E44AD"; // SUPERHELFER #00CED1
		case 4:
			return "#27AE60"; // HELFER
		default:
			return "#cccccc";
	}
};

// ------------------------------------------------------------
// Sortier-Priorität:
// 0 = INACTIVE
// 1 = aktiv in anderem Department
// 2 = ACTIVE
// 3 = Rest
// ------------------------------------------------------------
const getVolunteerSortPriority = (volunteer) => {
	if (!volunteer) return 3;

	const status = String(volunteer.status || "").toUpperCase();
	const isOther = volunteer.activeInOtherDepartment === true;

	if (status === "INACTIVE") return 0;
	if (isOther) return 1;
	if (status === "ACTIVE") return 2;

	return 3;
};

const VolunteerList = ({
	volunteers = [],
	setVolunteers,
	reloadVolunteers,
	onShiftChanged,
	okDepartment = ""
}) => {
	// ------------------------------------------------------------
	// States für UI / Modals
	// ------------------------------------------------------------
	const [loadingId, setLoadingId] = useState(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [removeId, setRemoveId] = useState(null);
	const [errorMessage, setErrorMessage] = useState(null);
	const [editingVolunteer, setEditingVolunteer] = useState(null);
	const [showCreateModal, setShowCreateModal] = useState(false);

	const isWrapperExpanded = showCreateModal || !!editingVolunteer;

	// ------------------------------------------------------------
	// Edit-Modal öffnen
	// ------------------------------------------------------------
	const handleOpenEditModal = (event, volunteer) => {
		event.preventDefault();
		event.stopPropagation();
		setEditingVolunteer(volunteer);
	};

	// ------------------------------------------------------------
	// Nach Save: Parent-Daten neu laden
	// ------------------------------------------------------------
	const handleEditSaved = async () => {
		try {
			await reloadVolunteers?.();
		} catch (err) {
			console.error("Fehler beim Reload nach Edit:", err);
		}
	};

	// ------------------------------------------------------------
	// Nach erfolgreichem Erstellen:
	// ------------------------------------------------------------
	const handleVolunteerCreated = async (newVolunteer) => {
		try {
			if (!newVolunteer) {
				await reloadVolunteers?.();
				return;
			}

			setVolunteers((prev) => {
				const exists = prev.some(
					(v) => (v.volunteerId ?? v.id) === (newVolunteer.volunteerId ?? newVolunteer.id)
				);

				if (exists) return prev;

				return [newVolunteer, ...prev];
			});

			await reloadVolunteers?.();
		} catch (err) {
			console.error("Fehler beim Create Flow:", err);
		} finally {
			setShowCreateModal(false);
		}
	};

	// ------------------------------------------------------------
	// Entfernen bestätigen
	// ------------------------------------------------------------
	const confirmRemove = async () => {
		if (!removeId || !setVolunteers) return;

		try {
			setLoadingId(removeId);
			setErrorMessage(null);

			await Api.delete(`/departments/remove-volunteer/${removeId}`);

			setVolunteers((prev) =>
				prev.filter((v) => (v.volunteerId ?? v.id) !== removeId)
			);

			setConfirmOpen(false);
			setRemoveId(null);
		} catch (err) {
			const status = err.response?.status;

			if (status === 403) {
				setErrorMessage("Keine Berechtigung.");
			} else if (status === 409) {
				setErrorMessage("Noch zugewiesen.");
			} else {
				setErrorMessage("Fehler.");
			}
		} finally {
			setLoadingId(null);
		}
	};

	// ------------------------------------------------------------
	// Sortierte Anzeige der Volunteers
	// ------------------------------------------------------------
	const sortedVolunteers = useMemo(() => {
		return [...volunteers].sort((a, b) => {
			const priorityA = getVolunteerSortPriority(a);
			const priorityB = getVolunteerSortPriority(b);

			if (priorityA !== priorityB) {
				return priorityA - priorityB;
			}

			const firstA = (a.firstName || "").trim().toLowerCase();
			const firstB = (b.firstName || "").trim().toLowerCase();

			const firstNameCompare = firstA.localeCompare(firstB, "de", {
				sensitivity: "base"
			});

			if (firstNameCompare !== 0) {
				return firstNameCompare;
			}

			const lastA = (a.lastName || "").trim().toLowerCase();
			const lastB = (b.lastName || "").trim().toLowerCase();

			return lastA.localeCompare(lastB, "de", {
				sensitivity: "base"
			});
		});
	}, [volunteers]);

	return (
		<div className={`volunteer-list-wrapper ${isWrapperExpanded ? "modal-open" : ""}`}>
			<div className="addButton">
				<button
					type="button"
					className="icon-button"
					onClick={() => setShowCreateModal(true)}
				>
					<img src={ADD_ICON} alt="Add" />
				</button>
				<span className="volunteer-count-badge">
					{sortedVolunteers.length}
				</span>
			</div>

			{!volunteers.length ? (
				<div className="no-volunteers-box">
					<p className="no-volunteers-text">
						Es sind noch keine Libellen vorhanden.
					</p>
				</div>
			) : (
				<ul className="volunteer-list">
					{sortedVolunteers.map((volunteer) => {
						const id = volunteer.volunteerId ?? volunteer.id;
						const isPoolEntry = typeof volunteer.status === "string";
						const roleId = getVolunteerRoleId(volunteer);

						let statusClass = "";

						if (roleId === ROLE_ID_SUPERHELFER) {
							statusClass = "status-superhelfer";
						} else if (isPoolEntry) {
							if (volunteer.activeInOtherDepartment) {
								statusClass = "status-active-other";
							} else if (volunteer.status === "ACTIVE") {
								statusClass = "status-active";
							} else {
								statusClass = "status-inactive";
							}
						}

						const canDelete =
							!isPoolEntry || volunteer.status === "INACTIVE";

						const roleColor = getRoleColor(roleId);

						return (
							<li
								key={id}
								className={`volunteer-card ${statusClass}`}
								style={{
									borderLeft: `6px solid ${roleColor}`
								}}
							>
								<span className="volunteer-card-name">
									<span className="volunteer-name">
										{volunteer.firstName} {volunteer.lastName}
									</span>

									{volunteer.phoneNumber && (
										<span className="volunteer-phone">
											{volunteer.phoneNumber}
										</span>
									)}
								</span>

								<div className="volunteer-card-actions">
									<button
										type="button"
										className="icon-btn edit-btn"
										onClick={(event) => handleOpenEditModal(event, volunteer)}
										onTouchEnd={(event) => handleOpenEditModal(event, volunteer)}
									>
										✏️
									</button>

									{canDelete && (
										<button
											type="button"
											className="icon-btn delete-btn"
											onClick={(event) => {
												event.preventDefault();
												event.stopPropagation();
												setRemoveId(id);
												setConfirmOpen(true);
											}}
											onTouchEnd={(event) => {
												event.preventDefault();
												event.stopPropagation();
												setRemoveId(id);
												setConfirmOpen(true);
											}}
											disabled={loadingId === id}
										>
											🗑️
										</button>
									)}
								</div>

								<div
									className="volunteer-status-dot"
									style={{ backgroundColor: roleColor }}
								/>
							</li>
						);
					})}
				</ul>
			)}

			{showCreateModal && (
				<div
					className="modal-backdrop modal-overlay-left-top"
					onClick={() => setShowCreateModal(false)}
				>
					<div
						className="modal-content-register"
						onClick={(e) => e.stopPropagation()}
					>
						<ManualVolunteerRegisterForm
							onVolunteerCreated={handleVolunteerCreated}
						/>

						<button
							type="button"
							className="modal-close-button"
							onClick={() => setShowCreateModal(false)}
						>
							schliessen
						</button>
					</div>
				</div>
			)}

			{editingVolunteer && (
				<div className="modal-overlay-inside modal-overlay-left-top">
					<EditVolunteerModal
						volunteer={editingVolunteer}
						okDepartment={okDepartment}
						onShiftChanged={onShiftChanged}
						onSaved={handleEditSaved}
						onClose={() => setEditingVolunteer(null)}
					/>
				</div>
			)}

			<ConfirmDeleteModal
				open={confirmOpen}
				loading={loadingId === removeId}
				errorMessage={errorMessage}
				onCancel={() => {
					setConfirmOpen(false);
					setRemoveId(null);
					setErrorMessage(null);
				}}
				onConfirm={confirmRemove}
			/>
		</div>
	);
};

export default VolunteerList;